import { useCallback, useRef } from 'react'
import { ANYWHERE_SEARCH_ITEM } from 'constants/search'
import { EXPERIENCE_TYPEAHEAD_SEARCH_TYPES } from 'constants/experience'
import { useTypeaheadSubmit } from 'hooks/Search/useTypeaheadSubmit'
import {
  createExperienceNaturalLanguageItem,
  isExperienceNaturalLanguageItem,
} from 'lib/search/experienceNaturalLanguageSearch'

/**
 * Resolves the item an experience form submits. Experience forms submit an unselected
 * typed phrase as a natural-language query; explicit non-query selections still win.
 * Transfer forms reuse experience controls but stay destination based, so they ignore
 * the typed phrase.
 */
export default function useExperienceNaturalLanguageSearch(isTransferSearch = false) {
  const submitRef = useRef<App.TypeaheadSubmitRefData | null>(null)
  const naturalLanguageSearch = !isTransferSearch
  const { dispatchTypeaheadExtraction } = useTypeaheadSubmit({
    enabled: naturalLanguageSearch,
    typeaheadTypes: EXPERIENCE_TYPEAHEAD_SEARCH_TYPES,
  })

  const getSearchItem = useCallback((selectedItem?: App.SearchItem, fallbackItem?: App.SearchItem): App.SearchItem => {
    if (!naturalLanguageSearch) return selectedItem ?? fallbackItem ?? ANYWHERE_SEARCH_ITEM
    if (selectedItem && !isExperienceNaturalLanguageItem(selectedItem)) return selectedItem

    const refData = submitRef.current
    const query = (selectedItem?.value ?? refData?.searchPhrase)?.trim()
    if (!query || !refData) return selectedItem ?? fallbackItem ?? ANYWHERE_SEARCH_ITEM

    const item = createExperienceNaturalLanguageItem(query, refData.typeaheadId)
    dispatchTypeaheadExtraction({ ...refData, searchPhrase: query }, item)
    return item
  }, [naturalLanguageSearch, dispatchTypeaheadExtraction])

  return { getSearchItem, temporarySearchSubmitRef: submitRef }
}
